import React from "react"
import { ShoppingCart } from "lucide-react"

import { OrderStatus } from "@/api/api-client"
import { OrderFilterBar } from "@/components/orders/OrderFilterBar"
import { EmptyState } from "@/components/ui/empty-state"
import { LoadingState } from "@/components/ui/loading-state"
import { VendorOrderCard } from "@/components/vendor/orders/VendorOrderCard"
import { VendorOrderQueueHeader } from "@/components/vendor/orders/VendorOrderQueueHeader"
import { useVendorOrderFilters } from "@/components/vendor/orders/useVendorOrderFilters"
import { useQuoteShipping, useUpdateOrderStatus, useVendorOrders } from "@/hooks/useOrders"
import { useLanguage } from "@/hooks/useLanguage"
import { toastError, toastSuccess } from "@/lib/toast"

interface VendorOrdersProps {
  focusedOrderId?: number | null
}

const VendorOrders: React.FC<VendorOrdersProps> = ({ focusedOrderId }) => {
  const { t } = useLanguage()
  const text = t.vendorOrders
  const { data: subOrders, isLoading } = useVendorOrders()
  const { mutate: updateStatus } = useUpdateOrderStatus()
  const { mutate: quoteShipping, isPending: isQuoting } = useQuoteShipping()

  const {
    filteredOrders,
    statusFilter,
    setStatusFilter,
    periodFilter,
    setPeriodFilter,
    searchQuery,
    setSearchQuery,
  } = useVendorOrderFilters({ orders: subOrders })

  const [shippingInputs, setShippingInputs] = React.useState<Record<number, string>>({})
  const [expandedWarehouseIds, setExpandedWarehouseIds] = React.useState<Set<number>>(new Set())
  const [highlightedOrderId, setHighlightedOrderId] = React.useState<number | null>(null)

  const toggleWarehouseAddress = React.useCallback((subOrderId: number) => {
    setExpandedWarehouseIds((prev) => {
      const next = new Set(prev)
      if (next.has(subOrderId)) {
        next.delete(subOrderId)
        return next
      }
      next.add(subOrderId)
      return next
    })
  }, [])

  const handleShippingInputChange = React.useCallback((subOrderId: number, value: string) => {
    setShippingInputs((prev) => ({ ...prev, [subOrderId]: value }))
  }, [])

  const handleStatusChange = React.useCallback(
    (subOrderId: number, newStatus: OrderStatus) => {
      updateStatus(
        { id: subOrderId, status: newStatus },
        {
          onSuccess: () => toastSuccess(text.updateSuccess),
          onError: () => toastError(text.updateError),
        }
      )
    },
    [text.updateError, text.updateSuccess, updateStatus]
  )

  const handleQuoteShipping = React.useCallback(
    (subOrderId: number) => {
      const raw = shippingInputs[subOrderId] ?? ""
      const cost = parseFloat(raw)
      if (isNaN(cost) || cost < 0) {
        toastError("Ingresa un costo de envío válido")
        return
      }

      quoteShipping(
        { id: subOrderId, shippingCost: cost },
        {
          onSuccess: () => {
            toastSuccess(text.updateSuccess)
            setShippingInputs((prev) => {
              const next = { ...prev }
              delete next[subOrderId]
              return next
            })
          },
          onError: () => toastError(text.updateError),
        }
      )
    },
    [quoteShipping, shippingInputs, text.updateError, text.updateSuccess]
  )

  React.useEffect(() => {
    if (!focusedOrderId) return
    setHighlightedOrderId(focusedOrderId)
    const scrollTimer = setTimeout(() => {
      document
        .getElementById(`order-${focusedOrderId}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" })
    }, 100)
    const clearTimer = setTimeout(() => setHighlightedOrderId(null), 2000)
    return () => {
      clearTimeout(scrollTimer)
      clearTimeout(clearTimer)
    }
  }, [focusedOrderId])

  if (isLoading) return <LoadingState label={text?.loading || "Loading..."} />

  const activeOrdersCount =
    subOrders?.filter(
      (order) =>
        order.status !== OrderStatus.OrderComplete && order.status !== OrderStatus.OrderDeclined
    ).length || 0
  const totalOrdersCount = subOrders?.length || 0

  return (
    <div className="space-y-8">
      <VendorOrderQueueHeader
        title={text?.queueTitle}
        subtitle={text?.queueSubtitle}
        metricsTemplate={text?.queueMetrics}
        activeOrdersCount={activeOrdersCount}
        totalOrdersCount={totalOrdersCount}
      />

      {subOrders && subOrders.length > 0 && (
        <OrderFilterBar
          className="border-b border-border/50 pb-6"
          statusValue={statusFilter}
          onStatusChange={setStatusFilter}
          statusOptions={[
            { value: "all", label: t.common.all || "All" },
            { value: "shipped", label: text.statuses[OrderStatus.OrderShipped] || "Shipped" },
            { value: "pending", label: text.statuses[OrderStatus.Pending] || "Pending" },
          ]}
          periodValue={periodFilter}
          onPeriodChange={setPeriodFilter}
          periodOptions={[
            { value: "30d", label: t.orderTracking.last30Days },
            { value: "3m", label: t.orderTracking.last3Months },
            { value: "2026", label: "2026" },
            { value: "2025", label: "2025" },
          ]}
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder={
            text.searchPlaceholder || t.order.searchPlaceholder || "Search order ID"
          }
          searchClassName="sm:w-48"
        />
      )}

      {filteredOrders.length === 0 ? (
        <EmptyState
          icon={ShoppingCart}
          message={
            subOrders && subOrders.length > 0
              ? t.order.notFound || "No matching orders found."
              : text?.empty || "No active orders"
          }
        />
      ) : (
        <div className="space-y-8">
          {filteredOrders.map((subOrder) => (
            <VendorOrderCard
              key={subOrder.id}
              subOrder={subOrder}
              labels={text}
              weightLabel={t.common.weight}
              dateFormat={t.common.shortDateFormat}
              highlighted={highlightedOrderId === subOrder.id}
              shippingInput={subOrder.id ? (shippingInputs[subOrder.id] ?? "") : ""}
              isQuoting={isQuoting}
              isWarehouseExpanded={
                subOrder.id !== undefined && expandedWarehouseIds.has(subOrder.id)
              }
              onToggleWarehouse={() =>
                subOrder.id !== undefined && toggleWarehouseAddress(subOrder.id)
              }
              onShippingInputChange={handleShippingInputChange}
              onQuoteShipping={handleQuoteShipping}
              onStatusChange={handleStatusChange}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default VendorOrders
